import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { streamUpdates } from 'lib-shared-api-utils';
import { createAuthorizationHeaderValue, QueryError } from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';

import { api } from 'app/app.api';

import {
  CreateAiAgentsApiArg,
  CreateAiAgentsApiResponse,
  CreateAiAgentsDuplicateApiArg,
  CreateAiAgentsToolApiArg,
  CreateAiAgentsToolApiResponse,
  CreateAiAgentsToolDuplicateApiArg,
  CreateAiAgentsToolsByIdApiArg,
  CreateAiAgentsToolsByIdApiResponse,
  CreateToolsDynamicSchemaRunApiArg,
  CreateToolsDynamicSchemaRunApiResponse,
  CreateToolsIntegrationsActionsRunApiArg,
  CreateToolsIntegrationsActionsRunApiResponse,
  DeleteAiAgentsApiArg,
  DeleteAiAgentsToolApiArg,
  DeleteAiAgentsToolApiResponse,
  ReadAiAgentsApiArg,
  ReadAiAgentsApiResponse,
  ReadAiAgentsDefaultsApiResponse,
  ReadAiAgentsKnowledgeBasesApiArg,
  ReadAiAgentsKnowledgeBasesApiResponse,
  ReadAiAgentsListApiArg,
  ReadAiAgentsListApiResponse,
  ReadAiAgentsToolApiArg,
  ReadAiAgentsToolApiResponse,
  ReadAiAgentsToolReferencesApiArg,
  ReadAiAgentsToolReferencesApiResponse,
  ReadAiAgentsToolsApiArg,
  ReadAiAgentsToolsApiResponse,
  ReadChatbotsQuotasApiArg,
  ReadChatbotsQuotasApiResponse,
  ReadToolsIntegrationsActionsApiArg,
  ReadToolsIntegrationsActionsApiResponse,
  UpdateAiAgentsApiArg,
  UpdateAiAgentsApiResponse,
} from './types/aiAgentsApi.types';
import type {
  RunAiAgentRequest,
  RunAiAgentSseEvent,
} from './types/runAiAgent.types';

export type AiAgentRunStreamResponse = {
  text: string;
  meta: {
    isLoading: boolean;
    isStreaming: boolean;
    isError: boolean;
    isSuccess: boolean;
  };
  feedback_id: string;
};

export const aiAgentsApi = api
  .enhanceEndpoints({
    addTagTypes: ['AiAgents', 'AiAgent', 'AiAgentTools', 'AiAgentTool'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // AI Agent CRUD
      createAiAgents: builder.mutation<
        CreateAiAgentsApiResponse,
        CreateAiAgentsApiArg
      >({
        query: ({ name, organizationId, knowledgeBases, user, variant }) => ({
          version: 1,
          path: 'ai-agents',
          method: 'POST',
          body: {
            name,
            organizationId,
            knowledgeBases,
            user,
            variant,
          },
        }),
        transformResponse: (response: unknown): CreateAiAgentsApiResponse => ({
          data: response as CreateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents'],
      }),
      readAiAgents: builder.query<ReadAiAgentsApiResponse, ReadAiAgentsApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}`,
        }),
        transformResponse: (response: unknown): ReadAiAgentsApiResponse => ({
          data: response as ReadAiAgentsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgent'],
      }),
      readAiAgentsList: builder.query<
        ReadAiAgentsListApiResponse,
        ReadAiAgentsListApiArg
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsListApiResponse => ({
          data: response as ReadAiAgentsListApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgents'],
      }),
      readAiAgentsKnowledgeBases: builder.query<
        ReadAiAgentsKnowledgeBasesApiResponse,
        ReadAiAgentsKnowledgeBasesApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/knowledge-bases`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsKnowledgeBasesApiResponse => ({
          data: response as ReadAiAgentsKnowledgeBasesApiResponse['data'],
          meta: {},
        }),
      }),
      updateAiAgents: builder.mutation<
        UpdateAiAgentsApiResponse,
        UpdateAiAgentsApiArg
      >({
        query: ({ id, ...rest }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'PATCH',
          body: rest,
        }),
        transformResponse: (response: unknown): UpdateAiAgentsApiResponse => ({
          data: response as UpdateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents', 'AiAgent'],
      }),
      deleteAiAgents: builder.mutation<void, DeleteAiAgentsApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      createAiAgentsDuplicate: builder.mutation<
        void,
        CreateAiAgentsDuplicateApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      readAiAgentsTools: builder.query<
        ReadAiAgentsToolsApiResponse,
        ReadAiAgentsToolsApiArg
      >({
        query: ({ id, offset, limit, type }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          params: { offset, limit, type },
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolsApiResponse => ({
          data: response as ReadAiAgentsToolsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readAiAgentsTool: builder.query<
        ReadAiAgentsToolApiResponse,
        ReadAiAgentsToolApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolApiResponse => ({
          data: response as ReadAiAgentsToolApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTool'],
      }),
      // Tools (all types)
      createAiAgentsTool: builder.mutation<
        CreateAiAgentsToolApiResponse,
        CreateAiAgentsToolApiArg
      >({
        query: ({ id, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          method: 'POST',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      createAiAgentsToolsById: builder.mutation<
        CreateAiAgentsToolsByIdApiResponse,
        CreateAiAgentsToolsByIdApiArg
      >({
        query: ({ id, toolId, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'PUT',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgentTool'],
      }),
      deleteAiAgentsTool: builder.mutation<
        DeleteAiAgentsToolApiResponse,
        DeleteAiAgentsToolApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      createAiAgentsToolDuplicate: builder.mutation<
        void,
        CreateAiAgentsToolDuplicateApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      readAiAgentsToolReferences: builder.query<
        ReadAiAgentsToolReferencesApiResponse,
        ReadAiAgentsToolReferencesApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/references`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolReferencesApiResponse => ({
          data: response as ReadAiAgentsToolReferencesApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readToolsIntegrationsActions: builder.query<
        ReadToolsIntegrationsActionsApiResponse,
        ReadToolsIntegrationsActionsApiArg
      >({
        query: ({ integrationType }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions`,
        }),
      }),
      createToolsIntegrationsActionsRun: builder.query<
        CreateToolsIntegrationsActionsRunApiResponse,
        CreateToolsIntegrationsActionsRunApiArg
      >({
        query: ({ integrationType, actionId, csid, configuredParameters }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions/${actionId}/run`,
          method: 'POST',
          params: !!csid ? { csid } : undefined,
          body: configuredParameters
            ? Object.entries(configuredParameters).map(([name, value]) => ({
                name,
                value: { value },
              }))
            : [],
        }),
      }),
      readAiAgentsDefaults: builder.query<
        ReadAiAgentsDefaultsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents/defaults',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsDefaultsApiResponse => ({
          data: response as ReadAiAgentsDefaultsApiResponse['data'],
          meta: {},
        }),
      }),
      readChatbotsQuotas: builder.query<
        ReadChatbotsQuotasApiResponse,
        ReadChatbotsQuotasApiArg
      >({
        query: ({ year, month }) => ({
          version: 1,
          path: 'chatbots/quotas',
          params: { year, month },
        }),
      }),
      createToolsDynamicSchemaRun: builder.query<
        CreateToolsDynamicSchemaRunApiResponse,
        CreateToolsDynamicSchemaRunApiArg
      >({
        query: ({ resolverId, value, name }) => ({
          version: 1,
          path: `tools/dynamic-schema/${resolverId}/run`,
          method: 'POST',
          body: { value, ...(name && { name }) },
        }),
      }),
      runAiAgent: builder.query<
        AiAgentRunStreamResponse,
        {
          agentId: string;
          dixaConversationId: string;
          userId: string;
          runKey: number;
        }
      >({
        queryFn: () =>
          ({
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
              feedback_id: '',
            },
          }) as MaybePromise<
            QueryReturnValue<AiAgentRunStreamResponse, QueryError>
          >,
        async onQueryStarted(
          { agentId, dixaConversationId, userId },
          { updateCachedData },
        ) {
          const conversationIdNum = Number(dixaConversationId);
          if (isNaN(conversationIdNum)) {
            updateCachedData((draft) => {
              draft.meta.isLoading = false;
              draft.meta.isError = true;
            });
            return;
          }

          let buffer = '';

          await streamUpdates<AiAgentRunStreamResponse>(
            updateCachedData,
            {
              url: `${config.url.rest}/v1/ai-agents/${agentId}/run`,
              body: {
                userId,
                dixaConversationId,
                session: {
                  _type: 'dixa-conversation-id',
                  id: conversationIdNum,
                },
                waitForUserInput: false,
                addMessagesToConversation: false,
              } satisfies RunAiAgentRequest,
              headers: {
                Authorization: await createAuthorizationHeaderValue(),
                Accept: 'text/event-stream',
              },
            },
            (draft, chunk) => {
              buffer += chunk;
              const events = buffer.split('\n\n');
              buffer = events.pop() ?? '';
              for (const raw of events) {
                const dataLine = raw
                  .split('\n')
                  .find((l) => l.startsWith('data: '));
                if (!dataLine) continue;
                try {
                  const event = JSON.parse(
                    dataLine.slice(6),
                  ) as RunAiAgentSseEvent;
                  if (event._type === 'message' && event.role === 'assistant') {
                    const textContent = event.content.find(
                      (c) => c._type === 'text',
                    );
                    if (textContent) {
                      draft.text = draft.text
                        ? draft.text + '\n' + textContent.text
                        : textContent.text;
                    }
                  }
                } catch {
                  // malformed SSE line, skip
                }
              }
            },
          );
        },
        keepUnusedDataFor: 0,
      }),
    }),
  });
